import { LanguageCode } from '../types';

/**
 * Utility to extract a localized string from a Record<LanguageCode, string> or plain string.
 */
export function getLocalized(
  field: string | Record<LanguageCode, string> | undefined | null,
  lang: LanguageCode,
  fallback: string = ''
): string {
  if (!field) return fallback;
  if (typeof field === 'string') return field;

  // Try selected lang -> KM -> FR -> EN -> TR -> fallback
  return (
    field[lang] ||
    field['KM'] ||
    field['FR'] ||
    field['EN'] ||
    field['TR'] ||
    fallback
  );
}

/**
 * Utility to localize standard category names
 */
export const CATEGORY_MAP: Record<string, Record<LanguageCode, string>> = {
  // General / Filters
  'Tüm': { KM: 'Hemû', FR: 'Tous', DE: 'Alle', EN: 'All', TR: 'Tüm' },
  'all': { KM: 'Hemû', FR: 'Tous', DE: 'Alle', EN: 'All', TR: 'Tüm' },

  // Activity / Culture Categories
  'Eğitim': { KM: 'Perwerde', FR: 'Éducation', DE: 'Bildung', EN: 'Education', TR: 'Eğitim' },
  'Sosyal Yardım': { KM: 'Alîkariya Civakî', FR: 'Aide Sociale', DE: 'Sozialhilfe', EN: 'Social Aid', TR: 'Sosyal Yardım' },
  'Kültürel': { KM: 'Çandî', FR: 'Culturel', DE: 'Kulturell', EN: 'Cultural', TR: 'Kültürel' },
  'Gençlik': { KM: 'Ciwanî', FR: 'Jeunesse', DE: 'Jugend', EN: 'Youth', TR: 'Gençlik' },
  'Kadın': { KM: 'Jin', FR: 'Femmes', DE: 'Frauen', EN: 'Women', TR: 'Kadın' },
  'Çocuk': { KM: 'Zarok', FR: 'Enfants', DE: 'Kinder', EN: 'Children', TR: 'Çocuk' },

  // News Categories
  'Duyurular': { KM: 'Agedarî', FR: 'Annonces', DE: 'Ankündigungen', EN: 'Announcements', TR: 'Duyurular' },
  'Dernek Haberleri': { KM: 'Nûçeyên Komelê', FR: 'Nouvelles de l\'Assoc.', DE: 'Vereinsnachrichten', EN: 'Association News', TR: 'Dernek Haberleri' },
  'Kültür & Sanat': { KM: 'Çand û Huner', FR: 'Culture & Art', DE: 'Kultur & Kunst', EN: 'Culture & Art', TR: 'Kültür & Sanat' },
  'Basın Açıklamaları': { KM: 'Raxistina Çapemeniyê', FR: 'Communiqués de Presse', DE: 'Pressemitteilungen', EN: 'Press Releases', TR: 'Basın Açıklamaları' },

  // Events Categories
  'Şenlik': { KM: 'Cejn & Festîval', FR: 'Festival', DE: 'Festival', EN: 'Festival', TR: 'Şenlik' },
  'Söyleşi': { KM: 'Hevpeyvîn', FR: 'Conférence', DE: 'Gespräch', EN: 'Panel Talk', TR: 'Söyleşi' },
  'Konser': { KM: 'Konser', FR: 'Concert', DE: 'Konzert', EN: 'Concert', TR: 'Konser' },
  'Atölye': { KM: 'Atolye', FR: 'Atelier', DE: 'Workshop', EN: 'Workshop', TR: 'Atölye' },
  'Anma': { KM: 'Bîranîn', FR: 'Commémoration', DE: 'Gedenken', EN: 'Commemoration', TR: 'Anma' },

  // Culture Specific
  'Tarih': { KM: 'Dîrok', FR: 'Histoire', DE: 'Geschichte', EN: 'History', TR: 'Tarih' },
  'Dil': { KM: 'Ziman', FR: 'Langue', DE: 'Sprache', EN: 'Language', TR: 'Dil' },
  'Müzik': { KM: 'Muzîk', FR: 'Musique', DE: 'Musik', EN: 'Music', TR: 'Müzik' },
  'Sanat': { KM: 'Huner', FR: 'Art', DE: 'Kunst', EN: 'Art', TR: 'Sanat' },
  'Edebiyat': { KM: 'Wêje', FR: 'Littérature', DE: 'Literatur', EN: 'Literature', TR: 'Edebiyat' },
  'Gelenekler': { KM: 'Kevneşopî', FR: 'Traditions', DE: 'Traditionen', EN: 'Traditions', TR: 'Gelenekler' },
  'Yemekler': { KM: 'Xwarin', FR: 'Gastronomie', DE: 'Küche', EN: 'Cuisine', TR: 'Yemekler' },
  'Kıyafetler': { KM: 'Kincên Gelêrî', FR: 'Habits Traditionnels', DE: 'Trachten', EN: 'Attire', TR: 'Kıyafetler' },

  // Document Categories
  'Tüzük': { KM: 'Rêzikname', FR: 'Statuts', DE: 'Satzung', EN: 'Bylaws', TR: 'Tüzük' },
  'Faaliyet Raporu': { KM: 'Rapora Çalakiyan', FR: 'Rapport d\'Activité', DE: 'Tätigkeitsbericht', EN: 'Activity Report', TR: 'Faaliyet Raporu' },
  'Formlar': { KM: 'Form', FR: 'Formulaires', DE: 'Formulare', EN: 'Forms', TR: 'Formlar' },
};

export function getLocalizedCategory(category: string, lang: LanguageCode): string {
  if (CATEGORY_MAP[category] && CATEGORY_MAP[category][lang]) {
    return CATEGORY_MAP[category][lang];
  }
  return category;
}
